import React, { useState } from "react";
import { Plus, CheckCircle, Clock, XCircle } from "lucide-react";
import { format } from "date-fns";
import PrimaryButton from "../ui/PrimaryButton";
import {
  usePlayerFacilities,
  useAddPlayerFacility,
  useApproveFacility,
} from "../../hooks/useAgentAssignments";
import { facilitiesService } from "../../api/services/facilities/facilitiesService";
import {
  PlayerFacility,
  FacilityStatus,
  CreatePlayerFacilityRequest,
} from "../../api/services/agentAssignment/types";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";

interface PlayerFacilitiesManagementProps {
  playerId?: string;
  assignmentId?: string;
  canApprove?: boolean;
}

export const PlayerFacilitiesManagement: React.FC<
  PlayerFacilitiesManagementProps
> = ({ playerId, assignmentId, canApprove = false }) => {
  const [showModal, setShowModal] = useState(false);
  const [selectedFacility, setSelectedFacility] =
    useState<PlayerFacility | null>(null);
  const [formData, setFormData] = useState<CreatePlayerFacilityRequest>({
    player_id: playerId || "",
    assignment_id: assignmentId,
    facility_category_id: "",
    facility_name: "",
    description: "",
    estimated_cost: undefined,
    actual_cost: 0,
    provided_date: new Date().toISOString().split("T")[0],
    vendor_name: "",
    invoice_number: "",
    notes: "",
  });

  // Fetch facilities
  const { data: facilitiesData, isLoading } = usePlayerFacilities({
    player_id: playerId,
    assignment_id: assignmentId,
  });

  // Fetch facility categories
  const { data: categoriesData } = useQuery({
    queryKey: ["facility-categories"],
    queryFn: () => facilitiesService.getCategories({}),
  });

  // Mutations
  const addFacilityMutation = useAddPlayerFacility();
  const approveFacilityMutation = useApproveFacility();

  const handleAddFacility = async () => {
    if (!formData.facility_category_id || !formData.facility_name) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await addFacilityMutation.mutateAsync({
        ...formData,
        player_id: playerId || formData.player_id,
        assignment_id: assignmentId || formData.assignment_id,
      });
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error("Error adding facility:", error);
    }
  };

  const handleApproveFacility = async (facilityId: string) => {
    try {
      await approveFacilityMutation.mutateAsync(facilityId);
    } catch (error) {
      console.error("Error approving facility:", error);
    }
  };

  const resetForm = () => {
    setFormData({
      player_id: playerId || "",
      assignment_id: assignmentId,
      facility_category_id: "",
      facility_name: "",
      description: "",
      estimated_cost: undefined,
      actual_cost: 0,
      provided_date: new Date().toISOString().split("T")[0],
      vendor_name: "",
      invoice_number: "",
      notes: "",
    });
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getStatusBadge = (status: FacilityStatus) => {
    switch (status) {
      case FacilityStatus.PENDING:
        return (
          <span className="flex items-center px-2 py-1 text-xs rounded-full bg-yellow-900/50 text-yellow-300 border border-yellow-700">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case FacilityStatus.APPROVED:
        return (
          <span className="flex items-center px-2 py-1 text-xs rounded-full bg-green-900/50 text-green-300 border border-green-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </span>
        );
      case FacilityStatus.REJECTED:
        return (
          <span className="flex items-center px-2 py-1 text-xs rounded-full bg-red-900/50 text-red-300 border border-red-700">
            <XCircle className="w-3 h-3 mr-1" />
            Rejected
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-white">Player Facilities</h3>
        <PrimaryButton onClick={() => setShowModal(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Add Facility
        </PrimaryButton>
      </div>

      {/* Facilities Table */}
      <div className="bg-slate-800 rounded-lg overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-12 text-center">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
            </div>
          </div>
        ) : facilitiesData?.items.length === 0 ? (
          <div className="px-6 py-12 text-center text-slate-400">
            No facilities added yet
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Facility
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                {canApprove && (
                  <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {facilitiesData?.items.map((facility) => (
                <tr
                  key={facility.id}
                  className="hover:bg-slate-700/50 transition-colors cursor-pointer"
                  onClick={() => setSelectedFacility(facility)}
                >
                  <td className="px-6 py-4">
                    <div>
                      <p className="text-white font-medium">
                        {facility.facility_name}
                      </p>
                      {facility.description && (
                        <p className="text-xs text-slate-400 mt-1">
                          {facility.description}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-slate-300">
                      {facility.facility_category?.name || "N/A"}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <p className="text-white">
                        {formatCurrency(facility.actual_cost)}
                      </p>
                      {facility.estimated_cost && (
                        <p className="text-xs text-slate-400">
                          Est: {formatCurrency(facility.estimated_cost)}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-slate-300">
                      {format(new Date(facility.provided_date), "MMM dd, yyyy")}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {getStatusBadge(facility.status)}
                  </td>
                  {canApprove && (
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      {facility.status === FacilityStatus.PENDING && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApproveFacility(facility.id);
                          }}
                          className="text-green-400 hover:text-green-300"
                        >
                          Approve
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Add Facility Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">Add Facility</h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Category *
                  </label>
                  <select
                    value={formData.facility_category_id}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        facility_category_id: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Category</option>
                    {categoriesData?.items?.map(
                      (category: { id: string; name: string }) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ),
                    )}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Facility Name *
                  </label>
                  <input
                    type="text"
                    value={formData.facility_name}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        facility_name: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., Hotel Accommodation"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Optional description..."
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Estimated Cost
                  </label>
                  <input
                    type="number"
                    value={formData.estimated_cost || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        estimated_cost: e.target.value
                          ? parseFloat(e.target.value)
                          : undefined,
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 50000"
                    min="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Actual Cost *
                  </label>
                  <input
                    type="number"
                    value={formData.actual_cost || ""}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        actual_cost: parseFloat(e.target.value) || 0,
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., 48000"
                    min="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Vendor Name
                  </label>
                  <input
                    type="text"
                    value={formData.vendor_name}
                    onChange={(e) =>
                      setFormData({ ...formData, vendor_name: e.target.value })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., ABC Hotel"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Invoice Number
                  </label>
                  <input
                    type="text"
                    value={formData.invoice_number}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        invoice_number: e.target.value,
                      })
                    }
                    className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                    placeholder="e.g., INV-12345"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Provided Date *
                </label>
                <input
                  type="date"
                  value={formData.provided_date}
                  onChange={(e) =>
                    setFormData({ ...formData, provided_date: e.target.value })
                  }
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={3}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Additional notes..."
                />
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-slate-700">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddFacility}
                disabled={addFacilityMutation.isPending}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {addFacilityMutation.isPending ? "Adding..." : "Add Facility"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Facility Details Modal */}
      {selectedFacility && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
          onClick={() => setSelectedFacility(null)}
        >
          <div
            className="bg-slate-800 rounded-lg max-w-2xl w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">
                Facility Details
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-400 mb-1">Facility Name</p>
                  <p className="text-white font-medium">
                    {selectedFacility.facility_name}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400 mb-1">Category</p>
                  <p className="text-white">
                    {selectedFacility.facility_category?.name || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400 mb-1">Actual Cost</p>
                  <p className="text-white">
                    {formatCurrency(selectedFacility.actual_cost)}
                  </p>
                </div>
                {selectedFacility.estimated_cost && (
                  <div>
                    <p className="text-sm text-slate-400 mb-1">
                      Estimated Cost
                    </p>
                    <p className="text-white">
                      {formatCurrency(selectedFacility.estimated_cost)}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-sm text-slate-400 mb-1">Provided Date</p>
                  <p className="text-white">
                    {format(
                      new Date(selectedFacility.provided_date),
                      "MMM dd, yyyy",
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-400 mb-1">Status</p>
                  <div className="mt-1">
                    {getStatusBadge(selectedFacility.status)}
                  </div>
                </div>
                {selectedFacility.vendor_name && (
                  <div>
                    <p className="text-sm text-slate-400 mb-1">Vendor</p>
                    <p className="text-white">{selectedFacility.vendor_name}</p>
                  </div>
                )}
                {selectedFacility.invoice_number && (
                  <div>
                    <p className="text-sm text-slate-400 mb-1">
                      Invoice Number
                    </p>
                    <p className="text-white">
                      {selectedFacility.invoice_number}
                    </p>
                  </div>
                )}
              </div>

              {selectedFacility.description && (
                <div>
                  <p className="text-sm text-slate-400 mb-1">Description</p>
                  <p className="text-white">{selectedFacility.description}</p>
                </div>
              )}

              {selectedFacility.notes && (
                <div>
                  <p className="text-sm text-slate-400 mb-1">Notes</p>
                  <p className="text-white">{selectedFacility.notes}</p>
                </div>
              )}

              {selectedFacility.approved_at && (
                <div className="pt-4 border-t border-slate-700">
                  <p className="text-sm text-slate-400">
                    Approved on{" "}
                    {format(
                      new Date(selectedFacility.approved_at),
                      "MMM dd, yyyy 'at' h:mm a",
                    )}
                  </p>
                </div>
              )}
            </div>

            <div className="flex gap-3 p-6 border-t border-slate-700">
              {canApprove &&
                selectedFacility.status === FacilityStatus.PENDING && (
                  <button
                    onClick={() => {
                      handleApproveFacility(selectedFacility.id);
                      setSelectedFacility(null);
                    }}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Approve Facility
                  </button>
                )}
              <button
                onClick={() => setSelectedFacility(null)}
                className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
